import { Timestamp } from 'firebase/firestore';
import i18n from 'i18next';
import { authClient } from '../auth/authClient';
import { notifyMutation } from './mutationSignal';
import { apiErrorKey } from './apiErrorKey';

/**
 * `message` est déjà traduit : les écrans l'affichent tel quel (#66 — ils
 * montraient « PUT /projects/p1 → 403 »). `status` et `code` (le `{ error }` de
 * kronos-api) restent pour les appelants qui décident sur l'échec ; `detail`
 * garde la requête pour la console.
 */
export class ApiError extends Error {
  constructor(public status: number, message: string, public code?: string, public detail?: string) {
    super(message);
    this.name = 'ApiError';
  }
}

// i18next non initialisé (tests unitaires) : la clé plutôt qu'un message vide.
const tr = (key: string) => (i18n.isInitialized ? i18n.t(key) : key);

async function apiFailure(res: Response, what: string): Promise<ApiError> {
  let code: string | undefined;
  try {
    code = ((await res.json()) as { error?: unknown }).error as string | undefined;
  } catch {
    // corps vide ou non JSON
  }
  return new ApiError(res.status, tr(apiErrorKey(res.status, code)), code, `${what} → ${res.status}`);
}

/** fetch qui transforme une coupure réseau (TypeError) en ApiError traduite. */
async function call(url: string, init: RequestInit, what: string): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch {
    throw new ApiError(0, tr(apiErrorKey(0)), undefined, `${what} → network`);
  }
}

/**
 * Revive Firestore Timestamps that the Admin SDK serialised to JSON
 * (`{_seconds,_nanoseconds}`) back into real `Timestamp` instances, so REST
 * reads match the SDK `onSnapshot`/`getDoc` shape and downstream code can call
 * `.toDate()`/`.toMillis()` unchanged. Reciprocal of the write-path revive in
 * `services/kronos-api/src/handler.ts`. Recurses arrays + plain objects.
 */
function reviveTimestamps(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(reviveTimestamps);
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    if (typeof o._seconds === 'number' && typeof o._nanoseconds === 'number') {
      return new Timestamp(o._seconds, o._nanoseconds);
    }
    // Also handle the client-write encoding, in case a value round-trips.
    if (o.type === 'firestore/timestamp/1.0' && typeof o.seconds === 'number' && typeof o.nanoseconds === 'number') {
      return new Timestamp(o.seconds, o.nanoseconds);
    }
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(o)) out[k] = reviveTimestamps(val);
    return out;
  }
  return v;
}

/**
 * Requêtes GET en vol, partagées par URL. Sans polling, tous les lecteurs d'un
 * écran se déclenchent au même instant (montage, retour d'onglet, écriture) :
 * `TrackerEntryRow` lisait `/api/tags` une fois PAR LIGNE (31 466 GET/24 h,
 * issue #63). Vidé à chaque écriture réussie, pour qu'une lecture lancée après
 * l'écriture ne récupère jamais une réponse partie avant.
 */
const inflight = new Map<string, Promise<unknown>>();

async function get<T>(url: string): Promise<T> {
  let p = inflight.get(url);
  if (!p) {
    p = (async () => {
      const token = await authClient.acquireToken();
      const res = await call(`/api${url}`, { headers: { Authorization: `Bearer ${token}` } }, `GET ${url}`);
      if (!res.ok) throw await apiFailure(res, `GET ${url}`);
      return reviveTimestamps(await res.json());
    })();
    const own = p;
    inflight.set(url, own);
    void own.finally(() => { if (inflight.get(url) === own) inflight.delete(url); }).catch(() => {});
  }
  const data = await p;
  // Copie par consommateur : un appelant qui trie en place ne doit pas toucher les autres.
  return (Array.isArray(data) ? [...data] : data) as T;
}

/** Lecture REST same-origin d'un document. Attache le Bearer Entra. */
export function apiGet<T>(path: string): Promise<T> {
  return get<T>(path);
}

function qs(params?: Record<string, string | number | undefined>): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined) sp.set(k, String(v));
  const s = sp.toString();
  return s ? `?${s}` : '';
}

async function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = await authClient.acquireToken();
  const res = await call(`/api${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  }, `${method} ${path}`);
  if (!res.ok) throw await apiFailure(res, `${method} ${path}`);
  inflight.clear();
  notifyMutation(); // only on success — a rejected write must not refresh anything
  // 200/201 with a JSON body → parse; empty body (e.g. delete) → undefined.
  const text = await res.text();
  return (text ? reviveTimestamps(JSON.parse(text)) : undefined) as T;
}

/** Collection/list read. Drops `undefined` params, returns the JSON array. */
export function apiList<T>(
  path: string,
  params?: Record<string, string | number | undefined>,
): Promise<T[]> {
  return get<T[]>(`${path}${qs(params)}`);
}

export const apiPost = <T>(path: string, body: unknown) => send<T>('POST', path, body);
export const apiPut = <T>(path: string, body: unknown) => send<T>('PUT', path, body);
export const apiDelete = (path: string) => send<void>('DELETE', path);
